import { createEntityAdapter, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'

import { sharedHydrated } from '@/store/shared-actions'

import type { ApprovalDecision, FinalDeliverable, ReviewAssignment, ReviewComment } from './types'

export const assignmentsAdapter = createEntityAdapter<ReviewAssignment>()
export const commentsAdapter = createEntityAdapter<ReviewComment>()
export const decisionsAdapter = createEntityAdapter<ApprovalDecision>()
export const deliverablesAdapter = createEntityAdapter<FinalDeliverable>()

export interface ReviewsState {
  assignments: EntityState<ReviewAssignment, string>
  comments: EntityState<ReviewComment, string>
  decisions: EntityState<ApprovalDecision, string>
  deliverables: EntityState<FinalDeliverable, string>
}

export const emptyReviews = (): ReviewsState => ({
  assignments: assignmentsAdapter.getInitialState(),
  comments: commentsAdapter.getInitialState(),
  decisions: decisionsAdapter.getInitialState(),
  deliverables: deliverablesAdapter.getInitialState(),
})

/**
 * Comments and decisions are add-only: no action here edits or removes one, so feedback and approvals stay as history.
 * Assignments change status (in review → approved / revision requested / withdrawn) but are never deleted.
 */
export const reviewsSlice = createSlice({
  name: 'reviews',
  initialState: emptyReviews(),
  reducers: {
    assignmentAdded(state, action: PayloadAction<ReviewAssignment>) {
      assignmentsAdapter.addOne(state.assignments, action.payload)
    },
    assignmentClosed(state, action: PayloadAction<{ id: string; status: Exclude<ReviewAssignment['status'], 'IN_REVIEW'>; at: string }>) {
      const { id, status, at } = action.payload
      assignmentsAdapter.updateOne(state.assignments, { id, changes: { status, closedAt: at } })
    },
    /** A reviewer hands the design to the approvers. `variationId` is the one they recommend, when there are variations. */
    assignmentShared(state, action: PayloadAction<{ id: string; at: string; byUserId: string; note: string; variationId: string | null }>) {
      const { id, at, byUserId, note, variationId } = action.payload
      assignmentsAdapter.updateOne(state.assignments, { id, changes: { sharedAt: at, sharedByUserId: byUserId, shareNote: note, variationId } })
    },
    commentAdded(state, action: PayloadAction<ReviewComment>) {
      commentsAdapter.addOne(state.comments, action.payload)
    },
    decisionRecorded(state, action: PayloadAction<ApprovalDecision>) {
      decisionsAdapter.addOne(state.decisions, action.payload)
    },
    deliverablesAdded(state, action: PayloadAction<FinalDeliverable[]>) {
      deliverablesAdapter.addMany(state.deliverables, action.payload)
    },
    deliverableReady(state, action: PayloadAction<{ id: string; at: string }>) {
      deliverablesAdapter.updateOne(state.deliverables, { id: action.payload.id, changes: { status: 'READY', readyAt: action.payload.at } })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedHydrated, (_state, action) => action.payload.data.reviews)
  },
})

export const { assignmentAdded, assignmentClosed, assignmentShared, commentAdded, decisionRecorded, deliverablesAdded, deliverableReady } = reviewsSlice.actions
